<template>
    <ac-modal
        :title="title"
        size-class="modal-md"
        ref="modal"
        class="search-plans-category-modal"
    >
        <template>
            <div class="search-plans-category-modal-body">
                <div v-if="availablePlanCategories.length > 0" class="search-plans-category-modal-buttons">
                    <button
                        v-for="planCategory in availablePlanCategories"
                        :key="planCategory.id"
                        type="button"
                        class="btn wl-btn-success search-plans-category-modal-btn"
                        @click="searchPlansForCategory(planCategory.id)"
                    >
                        {{ planCategory.description }}
                    </button>
                </div>
                <p v-else class="mb-0">No plan categories are available right now.</p>
            </div>
        </template>
    </ac-modal>
</template>

<script>
import {api} from '../../../global/framework/api.js';
import {common} from '../../../global/common.js';
import AcModal from '../../../global/customHtmlElements/AcModal.vue';

export default {
    name: 'SearchPlansCategoryModal',
    components: {AcModal},
    props: {
        consumerId: {type: Number, default: null},
        consultantId: {type: Number, default: null},
        companyId: {type: Number, default: null},
        token: {type: String, default: null},
        title: {type: String, default: 'Search For Plans'},
        /** When true, sets planCategoryId on the current route instead of navigating to member physician search. */
        useRouteQuery: {type: Boolean, default: false},
    },
    data() {
        return {
            addressId: null,
            availablePlanCategories: [],
        };
    },
    methods: {
        categoryIdFromRow(row) {
            if (row == null) {
                return null;
            }
            const id = row.id != null ? row.id : row.Id;
            return id != null ? Number(id) : null;
        },
        hasPlanCategoryInRoute() {
            const raw = this.$route.query.planCategoryId;
            return raw !== undefined && raw !== null && raw !== '';
        },
        async loadCategories() {
            // Always refetch: eligible categories depend on family member ages, which can
            // change while the app stays mounted (e.g. after a profile/DOB edit).
            if (this.companyId != null && this.token) {
                let url = `ProviderSearch/${this.companyId}/GetPlanCategories`;
                url = common.appendUrlParameter(url, 'token', this.token);
                this.availablePlanCategories = await $.ajax({type: 'GET', url});
            } else if (this.consultantId != null && this.consumerId != null) {
                this.availablePlanCategories = await api.getDataWithTwoIds(
                    'Psr', this.consultantId, 'AvailableCategoriesForConsumer', this.consumerId
                );
            } else if (this.consumerId != null) {
                this.availablePlanCategories = await api.getData(
                    'Consumer',
                    this.consumerId,
                    'AvailableCategoriesForConsumer',
                );
            } else {
                this.availablePlanCategories = [];
            }
            return this.availablePlanCategories;
        },
        navigateToPhysicianSearch(categoryId) {
            this.$router.push({
                name: 'physicianSearchByAddress',
                params: {
                    consumerId: this.consumerId,
                    radiusId: 7,
                    addressId: this.addressId,
                },
                query: {planCategoryId: categoryId, onlyAcceptingNewPatients: 1},
            });
        },
        applyCategoryToRoute(categoryId) {
            const currentPlanCategoryId = this.$route.query.planCategoryId;
            if (String(currentPlanCategoryId) === String(categoryId)) {
                return;
            }
            const nav = this.$router.push({
                path: this.$route.path,
                query: {
                    ...this.$route.query,
                    planCategoryId: categoryId,
                },
                hash: this.$route.hash,
            });
            if (nav && typeof nav.catch === 'function') {
                nav.catch(() => {});
            }
        },
        applyCategory(categoryId) {
            const id = this.categoryIdFromRow({id: categoryId});
            if (id == null) {
                return;
            }
            if (this.useRouteQuery) {
                this.applyCategoryToRoute(id);
            } else {
                this.navigateToPhysicianSearch(id);
            }
        },
        async open() {
            if (!this.useRouteQuery && this.consumerId != null && !this.addressId) {
                const consumer = await api.get('Consumer', this.consumerId, '');
                this.addressId = consumer.addressId;
            }
            await this.loadCategories();
            if (this.availablePlanCategories.length === 1) {
                const onlyId = this.categoryIdFromRow(this.availablePlanCategories[0]);
                if (onlyId != null) {
                    this.applyCategory(onlyId);
                } else {
                    this.$refs.modal.open();
                }
                return;
            }
            if (this.useRouteQuery && this.availablePlanCategories.length === 0) {
                return;
            }
            this.$refs.modal.open();
        },
        searchPlansForCategory(categoryId) {
            this.$refs.modal.close();
            this.applyCategory(categoryId);
        },
        async openOnLandingIfNeeded() {
            if (this.hasPlanCategoryInRoute()) {
                return;
            }
            await this.open();
        },
    },
};
</script>

<style scoped>
.search-plans-category-modal-body {
    text-align: center;
}

.search-plans-category-modal-buttons {
    display: flex;
    flex-direction: column;
    gap: 0;
    align-items: center;
}

.search-plans-category-modal-btn {
    width: 100%;
    max-width: 240px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    white-space: normal;
    border-radius: 999px;
    padding: 0.55rem 1.35rem;
    line-height: 1.25;
    margin: 0.4rem 0;
    transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
}

.search-plans-category-modal-btn:hover,
.search-plans-category-modal-btn:focus-visible {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
    filter: brightness(1.06);
}

.search-plans-category-modal-btn:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
    filter: brightness(0.98);
}

@media (prefers-reduced-motion: reduce) {
    .search-plans-category-modal-btn {
        transition: none;
    }

    .search-plans-category-modal-btn:hover,
    .search-plans-category-modal-btn:focus-visible {
        transform: none;
    }
}

.search-plans-category-modal :deep(.modal-dialog) {
    max-width: 320px;
}
</style>
